import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import api from "../lib/api";
import { CompetitorTable } from "../components/CompetitorTable";
import {
  ShieldCheck, Scale, Users, Sparkles, Star, TrendingUp, Clock, PoundSterling, ArrowRight, CheckCircle2, Check, X,
} from "lucide-react";

const fmt = (n) => `£${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtN = (n) => Number(n || 0).toLocaleString();

// Scroll-triggered ticking-up counter
function useCountUp(target, duration = 1400, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start || !target) { setValue(0); return; }
    let raf;
    const t0 = performance.now();
    const from = 0;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      // easeOutCubic for a natural settle
      const e = 1 - Math.pow(1 - p, 3);
      setValue(from + (target - from) * e);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return value;
}

function CountUp({ target, format = fmtN, className = "", testid, start }) {
  const value = useCountUp(target || 0, 1400, start);
  return <span className={className} data-testid={testid}>{format(Math.round(value))}</span>;
}

function StatCard({ label, value, sub, icon: Icon, accent = "white", testid }) {
  const accentClass = {
    pink: "text-nexus-pink",
    blue: "text-nexus-blue",
    purple: "text-nexus-purple",
    emerald: "text-emerald-300",
    yellow: "text-yellow-300",
    white: "text-white",
  }[accent] || "text-white";
  return (
    <div className="glass rounded-3xl p-6 card-hover" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${accentClass}`} />}
      </div>
      <div className={`font-display font-black text-4xl md:text-5xl tabular-nums ${accentClass}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-2">{sub}</div>}
    </div>
  );
}

export default function Trust() {
  const [data, setData] = useState(null);
  const [heroInView, setHeroInView] = useState(false);
  const [statsInView, setStatsInView] = useState(false);
  const heroRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    api.get("/trust/stats").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  useEffect(() => {
    if (!data) return;
    const obs = (el, setter) => {
      if (!el) return null;
      const o = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { setter(true); o.disconnect(); }
      }, { threshold: 0.35 });
      o.observe(el);
      return o;
    };
    const o1 = obs(heroRef.current, setHeroInView);
    const o2 = obs(statsRef.current, setStatsInView);
    return () => { o1 && o1.disconnect(); o2 && o2.disconnect(); };
  }, [data]);

  return (
    <div className="min-h-screen grain">
      <SEO
        title="Trust & Safety · Nexus"
        description="See how Nexus protects every collaboration — real escrow numbers, dispute resolution times, verified creators and community reviews."
        path="/trust"
      />
      <Navbar />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4 flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-nexus-pink" /> Trust & Safety
        </div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl">
          Every pound is <span className="nexus-gradient-text">protected</span>.
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl text-base sm:text-lg">
          Nexus holds brand money in escrow, releases only when work is approved, and reviews every dispute
          within days — not weeks. Live platform stats, updated in real time.
        </p>
      </section>

      {/* Escrow strip */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-4" ref={heroRef}>
        <div className="glass rounded-3xl p-8 md:p-10 relative overflow-hidden" data-testid="trust-escrow-strip">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-nexus-pink/10 blur-3xl rounded-full pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-nexus-blue/10 blur-3xl rounded-full pointer-events-none" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3 flex items-center gap-2">
              <PoundSterling className="h-3.5 w-3.5 text-emerald-400" /> Escrow held to date
            </div>
            <div className="font-display font-black text-6xl md:text-7xl lg:text-8xl tabular-nums nexus-gradient-text leading-none" data-testid="trust-total-escrowed">
              {data ? <CountUp target={data.escrow.total_escrowed_gbp} format={fmt} start={heroInView} /> : "£—"}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Released to creators</div>
                <div className="font-display font-bold text-2xl text-emerald-300 tabular-nums" data-testid="trust-released">
                  {data ? <CountUp target={data.escrow.released_gbp} format={fmt} start={heroInView} /> : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">In flight now</div>
                <div className="font-display font-bold text-2xl text-yellow-300 tabular-nums">
                  {data ? <CountUp target={data.escrow.in_flight_gbp} format={fmt} start={heroInView} /> : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Completed hires</div>
                <div className="font-display font-bold text-2xl text-white tabular-nums">
                  {data ? <CountUp target={data.escrow.hires_completed} start={heroInView} /> : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Active deals</div>
                <div className="font-display font-bold text-2xl text-white tabular-nums">
                  {data ? <CountUp target={data.escrow.hires_in_flight} start={heroInView} /> : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fee comparison strip */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4 flex items-center gap-2">
          <TrendingUp className="h-3.5 w-3.5 text-emerald-400" /> Fair pricing
        </div>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mb-6">
          Creators keep more with Nexus.
        </h2>
        <div className="glass rounded-3xl p-6 md:p-8" data-testid="trust-fee-comparison">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: "Nexus",  fee: "0%",  best: true,  note: "Creators keep 100% · escrow, disputes & payouts included" },
              { name: "Fiverr", fee: "20%",  note: "Plus service fees on brands" },
              { name: "Upwork", fee: "20%",  note: "Sliding to 10% after $10k" },
              { name: "Other platforms", fee: "15-30%", note: "Often + hidden processing fees" },
            ].map((p, i) => (
              <div
                key={p.name}
                data-testid={`fee-card-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`relative rounded-2xl p-5 flex flex-col justify-between transition-all ${p.best ? "bg-gradient-to-b from-nexus-pink/10 to-nexus-purple/5 border-2 border-nexus-pink/40" : "bg-white/[0.02] border border-white/5 opacity-70"}`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {p.best && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 nexus-gradient text-white text-[9px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                    Best deal
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{p.name}</div>
                  <div className={`font-display font-black text-4xl tabular-nums ${p.best ? "nexus-gradient-text" : "text-zinc-400"}`}>
                    {p.fee}
                  </div>
                </div>
                <div className={`mt-3 text-xs ${p.best ? "text-zinc-200" : "text-zinc-500"} flex items-start gap-1.5`}>
                  {p.best ? <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-400 shrink-0" /> : <X className="h-3.5 w-3.5 mt-0.5 text-zinc-600 shrink-0" />}
                  <span>{p.note}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-500 mt-5 text-center">
            Rates published on each platform&apos;s pricing page (Feb 2026). Creators pay nothing on Nexus — the platform fee is charged to the brand, so the price you agree is the amount you receive.
          </p>
        </div>
      </section>

      {/* Competitor comparison */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-8" data-testid="trust-compare-section">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Nexus vs the rest</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mb-6">How we compare to Trybe, Reacher, Refunnel, Superfiliate, Influee, Insense and Twirl.</h2>
        <CompetitorTable />
      </section>

      {/* Disputes */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4 flex items-center gap-2">
          <Scale className="h-3.5 w-3.5 text-nexus-pink" /> Dispute resolution
        </div>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mb-6">
          Fair. Fast. Human-reviewed.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Disputes resolved"
            value={data ? fmtN(data.disputes.resolved) : "—"}
            sub="Reviewed and ruled by Nexus admin"
            icon={CheckCircle2}
            accent="emerald"
            testid="trust-disputes-resolved"
          />
          <StatCard
            label="Avg resolution time"
            value={data && data.disputes.avg_resolution_hours != null ? `${data.disputes.avg_resolution_hours}h` : "—"}
            sub="From opened to final ruling"
            icon={Clock}
            accent="blue"
            testid="trust-avg-resolution"
          />
          <StatCard
            label="Currently open"
            value={data ? fmtN(data.disputes.open) : "—"}
            sub="Actively under review right now"
            icon={Scale}
            accent="pink"
            testid="trust-disputes-open"
          />
        </div>
      </section>

      {/* Community + reputation */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-8" ref={statsRef}>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4 flex items-center gap-2">
          <Users className="h-3.5 w-3.5 text-nexus-blue" /> The community
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Creators" value={data ? <CountUp target={data.community.creators} start={statsInView} /> : "—"} icon={Sparkles} accent="purple" testid="trust-creators" />
          <StatCard label="Verified creators" value={data ? <CountUp target={data.community.verified_creators} start={statsInView} /> : "—"} sub="Email · Identity · Socials" icon={ShieldCheck} accent="emerald" />
          <StatCard label="Brands" value={data ? <CountUp target={data.community.brands} start={statsInView} /> : "—"} icon={Users} accent="blue" />
          <StatCard label="Paying brands" value={data ? <CountUp target={data.community.paying_brands} start={statsInView} /> : "—"} sub="On Starter / Pro / Founding" icon={TrendingUp} accent="yellow" />
        </div>
      </section>

      {/* Reviews + case studies */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="glass rounded-3xl p-8" data-testid="trust-reviews">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-4">
              <Star className="h-3.5 w-3.5 text-[#FFEA00]" /> Community reviews
            </div>
            <div className="flex items-baseline gap-4">
              <div className="font-display font-black text-6xl text-white tabular-nums">
                {data ? data.reputation.average_rating.toFixed(2) : "—"}
              </div>
              <div className="text-[#FFEA00] text-2xl">★★★★★</div>
            </div>
            <div className="text-xs text-zinc-500 mt-3">
              Averaged across {data ? fmtN(data.reputation.reviews_count) : "—"} verified reviews — quality, communication, creativity, delivery speed and professionalism.
            </div>
          </div>
          <div className="glass rounded-3xl p-8 flex flex-col justify-between" data-testid="trust-case-studies">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-4">
                <Sparkles className="h-3.5 w-3.5 text-nexus-purple" /> Published case studies
              </div>
              <div className="font-display font-black text-6xl text-white tabular-nums">
                {data ? fmtN(data.case_studies.published) : "—"}
              </div>
              <div className="text-xs text-zinc-500 mt-3">Real brand × creator collaborations, published with both parties&apos; consent.</div>
            </div>
            <Link to="/case-studies" className="mt-6 inline-flex items-center gap-1.5 text-sm text-nexus-blue hover:text-white transition-colors group" data-testid="trust-case-studies-link">
              Browse case studies <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* How escrow works */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mb-8">How escrow works</h2>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { n: 1, title: "Brand funds", body: "Payment sits in Nexus escrow the moment a hire is agreed. Creators only start when the money is verified." },
            { n: 2, title: "Creator delivers", body: "Deliverables land in the deal room — brands see the work before a single penny moves." },
            { n: 3, title: "Brand approves", body: "One click releases 100% of the agreed price to the creator — no commission. Or request a revision at no cost." },
            { n: 4, title: "Dispute? We rule.", body: "Either side can escalate. Nexus reviews the deliverable, brief and full chat log, then rules fairly." },
          ].map((s) => (
            <div key={s.n} className="glass rounded-2xl p-6" data-testid={`trust-step-${s.n}`}>
              <div className="font-display font-black text-3xl nexus-gradient-text mb-3">{s.n}</div>
              <div className="font-display font-bold text-lg mb-2">{s.title}</div>
              <p className="text-sm text-zinc-400">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="glass rounded-3xl p-10 text-center border border-white/5">
          <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mb-3">Ready to work protected?</h3>
          <p className="text-zinc-400 max-w-xl mx-auto mb-6">Join the marketplace where every deal is escrow-backed, every dispute gets a human ruling, and every payout hits within days.</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/auth?mode=signup" className="nexus-gradient text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-opacity" data-testid="trust-cta-signup">
              Get started
            </Link>
            <Link to="/discover" className="rounded-full px-6 py-3 border border-white/10 text-white hover:bg-white/5 transition-colors" data-testid="trust-cta-browse">
              Browse creators
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-10 text-center text-xs text-zinc-600">
        {data ? `Stats generated ${new Date(data.generated_at).toLocaleString("en-GB")} · updated every 6 hours` : ""}
      </div>
    </div>
  );
}
